import { Dispatch } from 'redux'
import {
  getDocumentByContract,
  editDocumentTag,
  removeDocument,
  uploadDocument,
  updateDocumentType as updateDocumentsTypeAction,
} from 'app/state/ducks/documents/actions'
import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { ContractType } from 'app/models/common-types'
import { Document } from 'app/models/document-params'
import { coalesceThunkByKey } from 'app/state/utils/coalesce-thunk'

export const generatePresignedUrl = async (
  fileName: string,
  fileType: string,
  location?: string,
  id?: string,
) => {
  try {
    const apiServerUrl = config.backendUrl
    const presignedUrlResponse: any = await client.post(
      `${apiServerUrl}/api/documents/presigned/upload`,
      {
        fileName,
        fileType,
        location: location ?? 'lendos',
        id,
      },
    )
    return { success: true, payload: presignedUrlResponse.data }
  } catch (e: any) {
    console.error('Error generating presigned URL:', e)
    return { success: false, payload: e?.response?.data?.error }
  }
}

export const createDocument = (document: any) => async (dispatch: Dispatch) => {
  try {
    const apiServerUrl = config.backendUrl
    const response: any = await client.post(
      `${apiServerUrl}/api/documents/`,
      document,
    )
    dispatch(uploadDocument(response.data))
    return { success: true, payload: response.data }
  } catch (e: any) {
    console.error('Error generating presigned URL:', e)
    return { success: false, payload: e?.response?.data?.error }
  }
}

export const loadByContract = coalesceThunkByKey(
  (id: string, type: ContractType) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    try {
      const data = await client.get(
        `${apiServerUrl}/api/documents/${type}/${id}`,
      )
      return dispatch(getDocumentByContract({ data: data.data, type }))
    } catch (error) {
      console.log('THUNK ERROR', error)
    }
  },
)

export const changeTags =
  (id: string, newTags: string | null) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    try {
      const data = await client.post(`${apiServerUrl}/api/documents/edit-tag`, {
        id,
        newTags,
      })
      return dispatch(editDocumentTag(data.data))
    } catch (error) {
      console.log('THUNK ERROR', error)
    }
  }

export const deleteDocument = (ids: string) => async (dispatch: Dispatch) => {
  const apiServerUrl = config.backendUrl
  try {
    const data = await client.delete(
      `${apiServerUrl}/api/documents?ids[]=${ids}`,
    )
    dispatch(removeDocument(data))
    if (data.data?.length < 0) {
      return { success: false }
    }

    return {
      success: data.data[0].isSuccess,
      payload: data.data[0].isSuccess ? data.data[0].id : data.data[0].error,
    }
  } catch (e: any) {
    console.log('THUNK ERROR', e)
    return { success: false, payload: e?.response?.data?.error }
  }
}

export const updateDocumentType =
  ({ id, documentType, contractType }: Document, documentTypeText?: string) =>
  async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    try {
      const data = await client.put(
        `${apiServerUrl}/api/documents/edit-document-type`,
        {
          internalId: id,
          type: documentType,
          otherDocumentTypeText: documentTypeText,
        },
      )
      dispatch(
        updateDocumentsTypeAction({
          id,
          documentType,
          contractType,
          documentTypeText,
        }),
      )
      return { success: true, payload: data.data }
    } catch (e: any) {
      console.log('THUNK ERROR', e)
      return { success: false, payload: e?.response?.data?.error }
    }
  }

export const getDocumentFromS3 = async (id: string) => {
  try {
    const apiServerUrl = config.backendUrl
    const response = await client.get<{ url: string; document: any }>(
      `${apiServerUrl}/api/documents/download/${id}`,
    )
    return { payload: response.data, success: true }
  } catch (e: any) {
    console.error('THUNK ERROR', e)
    return { success: false, payload: e?.response?.data?.error }
  }
}

export const getSignatureFromS3 = async (id: string) => {
  try {
    const apiServerUrl = config.backendUrl
    const response = await client.get<{ url: string }>(
      `${apiServerUrl}/api/documents/download/signature/${id}`,
    )
    return { payload: response.data, success: true }
  } catch (e: any) {
    console.error('THUNK ERROR', e)
    return { success: false, payload: e?.response?.data?.error }
  }
}
